import {
  LitElement,
  html,
  nothing
} from 'lit';

import LdsIcon from '../LdsIcon/index.js';

import IconStyle from '../LdsIcon/style.js';
import LinkStyle from '../Common/LinkStyle.js';
import ButtonStyle from '../Common/ButtonStyle.js';

export class LdsLink extends LitElement {
  static get styles() {
    return [IconStyle, LinkStyle, ButtonStyle];
  }

  static properties = {
    ariaLabel: { type: String },
    contextualIcon: { type: Boolean },
    disableAutoAriaLabel: { type: Boolean },
    disableAutoIcon: { type: Boolean },
    disableButtonIconMargin: { type: Boolean },
    disableIconSpace: { type: Boolean },
    downloadable: { type: Boolean },
    external: { type: Boolean },
    href: { type: String },
    icon: { type: String },
    iconClasses: { type: String },
    iconDescription: { type: String },
    iconFill: { type: String },
    iconLabel: { type: String },
    iconPosition: { type: String },
    mode: { type: String },
    shadowClasses: { type: String },
    shadowClassesClearDefault: { type: Boolean },
    slotContent: { type: String },
    target: { type: String },
    targetAriaLabelContent: { type: String },
    useIcon: { type: Boolean },
  };

  // Default language resources will be included as hard text strings on applicable props below
  constructor() {
    super();
    this.ariaLabel = undefined;
    this.contextualIcon = false;
    this.disableAutoAriaLabel = false;
    this.disableAutoIcon = false;
    this.disableButtonIconMargin = false;
    this.disableIconSpace = false;
    this.downloadable = false;
    this.external = false;
    this.href = undefined;
    this.icon = undefined;
    this.iconClasses = undefined;
    this.iconDescription = undefined;
    this.iconFill = undefined;
    this.iconLabel = undefined;
    this.iconPosition = undefined;
    this.mode = undefined;
    this.shadowClasses = undefined;
    this.shadowClassesClearDefault = false;
    this.slotContent = undefined;
    this.target = undefined;
    this.targetAriaLabelContent = '(opens in a new tab)';
    this.useIcon = false;
  }

  /**
   * Event handler facilitates getting the content of the default slot for processing;
   * listens for the slotchange event to fire. Sets the data property 'slotContent'.
   * @param {*} e event
   */
  handleSlotchange(e) {
    const childNodes = e.target.assignedNodes({ flatten: true });
    this.slotContent = childNodes;
  }

  /**
   * Gets the inline link content which is normally just text content.
   *
   * Checks the first element in the slotContent constructor for a textContent object to return.
   * If it fails, returns undefined.
   *
   * @returns {string|undefined}
   */
  getLinkText() {
    let linkText = undefined;

    // TRY TO GET THE LINK TEXT
    try {
      // Link text should be whatever the textContent element of the first node in default component slot is
      linkText = this.slotContent[0].textContent;
      if (linkText) {
        // clean up any bookend whitespace in the slot content string
        linkText = linkText.trim();
      } else {
        // it seems that linkText and therefore the default slot is empty or undefined
        linkText = undefined;
      }
    } catch (ex) {
      // If we run into a failure getting the text, usually due to complex content in the slot, fail silently
      linkText = undefined;
    }

    return linkText;
  }

  /**
   * Gets the link mode attribute if set; only valid modes are allowed.
   *
   * The default mode is 'link' for a regular text link.
   *
   * @returns {string}
   */
  getMode() {
    let mode = undefined;

    // Fail fast input validation
    if (this.mode) {
      try {
        mode = this.mode.toLowerCase();
      } catch (ex) {
        // eslint-disable-next-line no-console
        console.error('ERROR: LDS Link failed to process mode input value: \'%s\'; mode will be the default.\nException:', this.mode, ex.message);
        return 'link';
      }
    }

    if (mode) {
      switch (mode) {
      case 'link':
      case 'button':
        return mode;
      default:
        // Target input value is invalid, do not set target, report warning
        // eslint-disable-next-line no-console
        console.warn('WARNING: LDS Link set with unexpected invalid mode value of \'%s\' which has been ignored; mode will be the default', this.mode);
        return 'link';
      }
    }

    // Mode is simply not set
    return 'link';
  }

  /**
   * Gets the link target attribute if set; only valid HTML target values are allowed;
   * see: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target.
   * If the target is set and is valid, returns the target value string,
   * otherwise it returns undefined.
   * @returns {string|undefined}
   */
  getTarget() {
    let target = undefined;

    // Fail fast input validation
    if (this.target) {
      try {
        target = this.target.toLowerCase();
      } catch (ex) {
        // eslint-disable-next-line no-console
        console.error('ERROR: LDS Link failed to process target input value: \'%s\'; target will not be set.\nException:', this.target, ex.message);
        return undefined;
      }
    }

    // HTML Spec input validation
    if (target) {
      switch (target) {
      case '_self':
      case '_blank':
      case '_parent':
      case '_top':
        // Target input value is valid per HTML spec for this attribute
        return target;
      default:
        // Target input value is invalid, do not set target, report warning
        // eslint-disable-next-line no-console
        console.warn('WARNING: LDS Link set with unexpected invalid target value of \'%s\' which has been ignored; target not set. Only HTML spec target values are allowed, see: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target', this.target);
        return undefined;
      }
    }

    // Target is simply not set
    return undefined;
  }

  /**
   * Automatic ARIA Label Function --
   * When the target attribute will be set, gets a descriptive text string value for the target
   * behavior which will be added to the ARIA Label text.
   * If getTarget is defined, and the data property targetAriaLabelContent is defined
   * returns that data property string value, otherwise returns undefined.
   * @returns {string|undefined}
   */
  getTargetAriaLabelContent() {
    if ((this.getTarget() && this.getTarget() !== '') && (this.targetAriaLabelContent)) {
      return this.targetAriaLabelContent;
    }

    return undefined;
  }

  /**
   * Automatic ARIA Label Function --
   * Gets the value of a downloadable file's extension.
   * Expects the link href to include a resource with a dot-extension
   * (e.g. http://domain.com/file.ext)
   * If successful, the extension is made uppercase and returned as a string in parenthesis.
   * If invalid or it fails, undefined is returned.
   * @returns {string|undefined}
   */
  getDownloadableFileType() {
    if (this.href && this.downloadable) {
      try {
        const filePathParts = this.href.split('.');
        // Make sure the filetype has an extension (or something after a period which should be an extension)
        if (filePathParts.length > 1) {
          const fileType = `(${filePathParts.pop().toUpperCase()})`;

          // So, if this returns something unexpected like a space
          // Or a bunch of stuff after part of the domain name because there's no final file extension
          // The developer should come to their own conclusion that the 'downloadable' feature doesn't work for thier use case
          // However, don't return empty string (which is falsey)
          if (fileType) return fileType;

          // Fallback to undefined if fileType is falsey
          return undefined;
        }
      } catch {
        // GNDN - no reason to break the component if this fails
        return undefined;
      }
    }

    // No href to process, or not downloadable
    return undefined;
  }

  /**
   * Automatic ARIA Label Function --
   * Gets the value to set on the aria-label attribute.
   * When user defined via the ariaLabel prop, all automatic functions are ignored.
   * When the disableAutoAriaLabel prop is set, all automatic functions are ignored;
   * the aria-label attribute will not be set (returns undefined).
   * Otherwise, it defaults to attempting to set the attribute value "intelligently" using the
   * link text and other prop configurations for enhanced accessibility compliance.
   * @returns {string|undefined}
   */
  getAriaLabel() {
    // Don't use the aria-label attribute at all
    if (this.disableAutoAriaLabel) return undefined;

    // Use the user-defined aria-label attribute
    if (this.ariaLabel) return this.ariaLabel;

    // Get the "link text" content of the default slot, if it exists
    const linkText = this.getLinkText();

    // If we didn't get any link text, bail out; we cannot automatically set an accessibility value
    if (!linkText) return undefined;

    // CHECK FOR COMPONENT CONFIGURATIONS THAT WILL "ENHANCE" THE AUTOMATIC ARIA LABEL

    // Process when the downloadable prop is set
    if (this.getDownloadableFileType()) {
      // If the downloadable prop is set and a file type is detected, append it
      if (this.getTargetAriaLabelContent()) {
        // If the downloadable link is going to open in a new tab or windo
        // the aria label should ALSO include text that indicates this
        return `${linkText}${' ' + this.getDownloadableFileType()}${' ' + this.getTargetAriaLabelContent()}`;
      }
      // Otherwise, if it's a regular download link, append the file type name
      return `${linkText}${' ' + this.getDownloadableFileType()}`;
    }

    // Process when the target attribute is set
    if (this.getTargetAriaLabelContent()) {
      // If the link is going to open in a new tab or window include text
      // the aria label should repeat the link text and include text that indicates the behavior.
      return `${linkText}${' ' + this.getTargetAriaLabelContent()}`;
    }

    // Finally, just set the link text as the aria label
    return linkText;
  }

  /**
   * Gets the CSS class values for the link.
   * User defined classes via the 'class' prop will be appended to the defaults.
   * Default is: "lds-link".
   * If the clearDefaultClasses prop is set no default/programmatic classes will be set.
   * Returns the undefined primitive when clearDefaultClasses is set with no user defined classes set.
   * @returns {string|undefined}
   */
  getCssClasses() {
    let defaultClasses = 'lds-link';

    switch (this.getMode()) {
    case 'button':
      defaultClasses = 'lds-button';
      break;
    case 'link':
    default:
      defaultClasses = 'lds-link';
    }

    // Disabled defaults with custom values
    if (this.shadowClassesClearDefault && this.shadowClasses) return this.shadowClasses;

    // Disabled defaults with no custom values
    if (this.shadowClassesClearDefault && !this.shadowClasses) return undefined;

    // Use defaults with custom values
    if (this.shadowClasses) return `${defaultClasses} ${this.shadowClasses}`;

    // Fallback to always use defaults
    return defaultClasses;
  }

  /**
   * Gets the custom LDS icon name to use based on prop configurations.
   * Can be user-defined by the 'icon' data property.
   * The value is not guaranteed to be a valid LDS Icon name when user-defined.
   * Always expected to return a string value; falls back to the icon name 'link'.
   * @returns {string}
   */
  getIconName() {
    // When the user has explicitly set the icon name, always override all defaults
    if (this.icon) return this.icon;

    // With downloadable flag, set the "download" icon as default (supersedes external even if external is also set)
    if (this.downloadable) return 'DownloadSimple';

    // With external flag, set the "externalLink" icon as default
    if (this.external) return 'ArrowSquareOut';

    // Default to generic "link" icon when no other options are set
    return 'Link';
  }

  /**
   * Gets the custom LDS icon position to use based on prop configurations.
   * Can be user-defined by the 'iconPosition' data property.
   * Validates that, when user-defined the value is valid.
   * Always expected to return a string value.
   * If user-defined and invalid it will fall-back to the best configuration default.
   * @returns {string}
   */
  getIconPosition() {
    // Set the position if the user has explicitly set the prop, if the prop has a valid value
    // This will also allow overriding the default for auto icons
    if (this.iconPosition) {
      try {
        switch (this.iconPosition.toLowerCase()) {
        case 'before':
        case 'after':
          // Position input is valid
          return this.iconPosition;
        default:
          // Position value is invalid, do not set position, report usage problem to developer as a warning of unexpected behavior
          // eslint-disable-next-line no-console
          console.warn('WARNING: LDS Link set with unexpected invalid icon position value of \'%s\' which has been ignored; the default will be used. Valid values are \'before\' and \'after\' which places the icon before or after the link content respectively.', this.iconPosition);
        }
      } catch (ex) {
        // Exception occurred, do not set position, report unexpected error to developer; likely something that wasn't a string got passed somehow?
        // eslint-disable-next-line no-console
        console.error('ERROR: LDS Link failed to process icon position value: \'%s\'; the default will be used.\nException:', this.iconPosition, ex.message);
      }
    }

    // The default position for the external or downloadable icons should be "after"
    if (this.external || this.downloadable) return 'after';

    // Otherwise, the default icon position is "before"
    return 'before';
  }

  /**
   * Determines whether to render the custom LDS icon at all based on prop configurations.
   * Checks for "automatic" icon use cases to enable the custom LDS icon.
   * Is explcitly always true when the user-defined data property 'useIcon' is true.
   * @returns {boolean}
   */
  shouldRenderIcon() {
    // Links flagged external automatically get an external icon unless explicitly disabled
    if (this.external && !this.disableAutoIcon) return true;

    // Links flagged downloadable automatically get a download icon unless explicitly disabled
    if (this.downloadable && !this.disableAutoIcon) return true;

    // If the user explicitly wants to use the icon feature, render the icon
    if (this.useIcon) return true;

    // Fallback default
    return false;
  }

  /**
   * Encapsulates final render logic to determine if the icon will be rendered at all, and if so
   * whether it will be BEFORE the slot content.
   * Always false if getIconPosition does not return "before".
   * @returns {boolean}
   */
  shouldRenderIconBefore() {
    if (this.shouldRenderIcon() && this.getIconPosition() === 'before') return true;
    return false;
  }

  /**
   * Encapsulates final render logic to determine if the icon will be rendered at all, and if so
   * whether it will be AFTER the slot content.
   * Always false if getIconPosition does not return "after".
   * @returns {boolean}
   */
  shouldRenderIconAfter() {
    if (this.shouldRenderIcon() && this.getIconPosition() === 'after') return true;
    return false;
  }

  /**
   * Determine if the icon should include a non-breaking space character before or after it and
   * generate the correct output
   *
   * @returns {html|string}
   */
  iconTrailer() {
    // If there's no discernable plain text or inline content in the default slot; no space.
    // Either the only content is the icon or the content is complex, the user can add a space
    if (!this.getLinkText()) return '';

    // Standard text links
    if (this.getMode() === 'link') {
      if (this.disableIconSpace) return '';
      return html`&nbsp;`;
    }

    // Button links will use margins between icons unless explicitly disabled
    if (this.getMode() === 'button') {
      // Always disable the space when using the icon margins
      if (!this.disableButtonIconMargin) return '';
      // If icon margins are disabled, assume the space is desired
      if (this.disableButtonIconMargin && !this.disableIconSpace) return html`&nbsp;`;
      // If icon margin and icon space are disabled explicitly, do it
      if (this.disableButtonIconMargin && this.disableIconSpace) return '';
    }

    // Fallback to add the trailer space if the mode isn't explicitly handled
    return html`&nbsp;`;
  }

  /**
   * Gets the ICON CSS class values to decorate the link when it is using an icon. Icon
   * decorator classes are all optional and based on data props the user can configure.
   *
   * @returns {string|undefined}
   */
  getIconCssClasses() {
    // In default text link mode, only add any user defined icon classes
    if (this.getMode() === 'link') {
      if (this.iconClasses) return this.iconClasses;
      return '';
    }

    // In button mode icons will have some default classes
    if (this.getMode() === 'button') {
      let buttonIconClasses = 'button-icon';

      // If there's discernable link text and buton icon margin isn't explictily disabled; add the icon margins
      if (this.getLinkText() && !this.disableButtonIconMargin) {
        if (this.getIconPosition() === 'before') buttonIconClasses += ' before';
        if (this.getIconPosition() === 'after') buttonIconClasses += ' after';
      }

      // Add any user defined icon component classes
      if (this.iconClasses) buttonIconClasses += ` ${this.iconClasses}`;

      return buttonIconClasses;
    }

    // If mode check fails, just try to return any user-defined icon classes
    return this.iconClasses;
  }

  /**
   * Renders the icon HTML
   * @returns {html}
   */
  renderIconHtml() {
    // Set the standard icon HTML, add no extraneous linebreaks to avoid inadvertant whitespace char injection
    // Icon must always be inline
    // Icon is by default decorative unless the contextualIcon property is true
    const iconHtml = html`<lds-icon
      class="${this.getIconCssClasses() || nothing}"
      name="${this.getIconName() || 'Link'}"
      label="${this.iconLabel || nothing}"
      description="${this.iconDescription || nothing}"
      fill="${this.iconFill || nothing}"
      inline
      ?decorative="${!this.contextualIcon}"
      ></lds-icon>`;

    // When the icon position is "before" add a trailing non-breaking space unless that is disabled
    if (this.shouldRenderIconBefore()) return html`${iconHtml}${this.iconTrailer()}`;

    // When the icon position is "after" add a preceeding non-breaking space unless that is disabled
    if (this.shouldRenderIconAfter()) return html`${this.iconTrailer()}${iconHtml}`;

    // If no icon is to be displayed...
    return nothing;
  }

  /**
   * Renders the downloadable file type extension content HTML
   * @returns {html}
   */
  renderDownloadableHtml() {
    // If there's a downloadable file type return the string type value preceeded with a non-breaking space
    if (this.getDownloadableFileType()) return html`&nbsp;${this.getDownloadableFileType()}`;

    // Otherwise...
    return nothing;
  }

  /**
   * FINAL RENDER
   * NOTE: The inner HTML has to watch it's line breaks at the end or it introduces whitespace
   * which leads to underline-styled trailing spaces which ain't nobody got time for
   * @returns {html}
   */
  /* eslint-disable indent */
  render() {
    return html`<a
        class="${this.getCssClasses() || nothing}"
        href="${this.href || nothing}"
        target="${this.getTarget() || nothing}"
        external="${this.external ? 'true' : nothing}"
        aria-label="${this.getAriaLabel() || nothing}"
      >${
          this.shouldRenderIconBefore() ? this.renderIconHtml() : nothing
        }<slot @slotchange=${this.handleSlotchange}></slot>${
          this.renderDownloadableHtml()
        }${
          this.shouldRenderIconAfter() ? this.renderIconHtml() : nothing
        }</a>`;
  }
  /* eslint-enable indent */
}
export function defineComponent() {
  if (!window.customElements.get('lds-link')) window.customElements.define('lds-link', LdsLink);
  LdsIcon.defineComponent();
}

export default { LdsLink, defineComponent };
